import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { motion } from 'framer-motion';
import { CheckCircle, CreditCard, ArrowLeft, ShieldCheck, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { t, lang } = useLanguage();
  const { items, subtotal, taxes, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', pickup_time: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    axios.get(`${API}/business-hours`).then(r => setIsOpen(r.data.is_open)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (items.length === 0 && !orderResult) {
    navigate('/cart');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const orderItems = items.map(i => {
        const modsPrice = (i.modifiers || []).reduce((s, m) => s + (m.price || 0), 0);
        return {
          id: i.menuItem.id,
          name: lang === 'fr' ? i.menuItem.name_fr : i.menuItem.name_en,
          price: i.menuItem.price + (i.selectedOption?.price_modifier || 0) + modsPrice,
          quantity: i.quantity,
          option: i.selectedOption ? (lang === 'fr' ? i.selectedOption.name_fr : i.selectedOption.name_en) : null,
          modifiers: (i.modifiers || []).map(m => ({
            type: m.type,
            name: lang === 'fr' ? m.name_fr : m.name_en,
            price: m.price || 0,
          })),
        };
      });
      const { data } = await axios.post(`${API}/orders`, {
        ...form,
        items: orderItems,
        total: total,
        pickup_time: form.pickup_time,
        order_type: 'takeout',
        lang: lang,
      });
      setOrderResult(data);
      clearCart();
      toast.success(t('checkout.success'));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (lang === 'fr' ? 'Erreur lors de la commande' : 'Error placing order'));
    } finally {
      setLoading(false);
    }
  };

  if (orderResult) {
    return (
      <div className="pt-20 lg:pt-24 pb-20 min-h-[60vh] flex items-center justify-center" data-testid="order-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md mx-auto px-4">
          <CheckCircle className="w-20 h-20 text-[#2E7D32] mx-auto mb-6" />
          <h2 className="font-heading text-3xl font-bold text-[#1a1a1a] mb-3">{t('checkout.success')}</h2>
          <p className="text-[#555555] mb-4">{t('checkout.successMsg')}</p>
          <div className="bg-white rounded-xl border border-[#e0e0e0] p-6 mb-6">
            <p className="text-sm text-[#555555] mb-1">{t('checkout.orderNumber')}</p>
            <p className="text-2xl font-bold text-[#e53a3a]">{orderResult.order_number}</p>
          </div>
          <Link to="/" className="btn-belgo inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="back-home">
            {lang === 'fr' ? 'Retour à l\'accueil' : 'Back to Home'}
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-20 lg:pt-24 pb-20" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {!isOpen && (
          <div className="mb-8 bg-black text-white rounded-xl p-5 flex items-center gap-4" data-testid="checkout-closed-banner">
            <Clock className="w-8 h-8 text-[#f4d00d] shrink-0" />
            <div>
              <p className="font-semibold text-lg">
                {lang === 'fr' ? 'Le restaurant est présentement fermé' : 'The restaurant is currently closed'}
              </p>
              <p className="text-sm text-gray-300">
                {lang === 'fr'
                  ? 'Les commandes pour emporter ne peuvent pas être passées en ce moment. Revenez pendant les heures d\'ouverture.'
                  : 'Takeout orders cannot be placed right now. Come back during business hours.'}
              </p>
            </div>
          </div>
        )}
        <Link to="/cart" className="inline-flex items-center gap-2 text-sm text-[#555555] hover:text-[#e53a3a] mb-6" data-testid="back-to-cart">
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </Link>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#1a1a1a] tracking-tight mb-8">{t('checkout.title')}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-[#e0e0e0] p-8 space-y-5" data-testid="checkout-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label>{t('checkout.name')}</Label>
                  <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} required className="mt-1" data-testid="checkout-name" />
                </div>
                <div>
                  <Label>{t('checkout.email')}</Label>
                  <Input type="email" value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))} required className="mt-1" data-testid="checkout-email" />
                </div>
                <div>
                  <Label>{t('checkout.phone')}</Label>
                  <Input type="tel" value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))} required className="mt-1" data-testid="checkout-phone" />
                </div>
                <div>
                  <Label>{t('checkout.pickupTime')}</Label>
                  <Input type="time" value={form.pickup_time} onChange={e => setForm(f => ({ ...f, pickup_time: e.target.value }))} required className="mt-1" data-testid="checkout-time" />
                </div>
              </div>
              <div>
                <Label>{t('checkout.notes')}</Label>
                <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} className="mt-1" data-testid="checkout-notes" />
              </div>

              {/* Payment Info */}
              <div className="bg-[#f5f5f0] rounded-lg p-4 border border-[#e0e0e0]" data-testid="payment-info">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-5 h-5 text-[#2E7D32]" />
                  <span className="font-semibold text-sm text-[#1a1a1a]">{t('checkout.payment')}</span>
                </div>
                <p className="text-xs text-[#555555]">{t('checkout.paymentNote')}</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-belgo w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-white font-semibold text-lg"
                style={{ backgroundColor: '#e53a3a' }}
                data-testid="place-order-btn"
              >
                <CreditCard className="w-5 h-5" /> {loading ? t('checkout.processing') : t('checkout.placeOrder')}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-[#e0e0e0] p-6 sticky top-24" data-testid="order-summary">
              <h3 className="font-heading text-lg font-semibold text-[#1a1a1a] mb-4">{lang === 'fr' ? 'Résumé' : 'Summary'}</h3>
              <div className="space-y-3 mb-4">
                {items.map(item => {
                  const name = lang === 'fr' ? item.menuItem.name_fr : item.menuItem.name_en;
                  const modsPrice = (item.modifiers || []).reduce((s, m) => s + (m.price || 0), 0);
                  const price = item.menuItem.price + (item.selectedOption?.price_modifier || 0) + modsPrice;
                  return (
                    <div key={item.key} className="text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#555555]">{item.quantity}x {name}</span>
                        <span className="font-medium">{(price * item.quantity).toFixed(2)} $</span>
                      </div>
                      {(item.modifiers || []).length > 0 && (
                        <ul className="ml-4 mt-1 text-xs text-[#888]">
                          {item.modifiers.map((m, idx) => (
                            <li key={idx}>+ {lang === 'fr' ? m.name_fr : m.name_en}{m.price > 0 ? ` (+${m.price.toFixed(2)} $)` : ''}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
              <Separator className="my-3" />
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#555555]">{t('cart.subtotal')}</span>
                  <span>{subtotal.toFixed(2)} $</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#555555]">{t('cart.taxes')}</span>
                  <span>{taxes.toFixed(2)} $</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>{t('cart.total')}</span>
                  <span className="text-[#e53a3a]">{total.toFixed(2)} $</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
